import React from "react";
import "./errorBoundary.css";

// Non-cleared, non-component-state: whatever just threw is about to be
// thrown away and rebuilt wholesale by the reload below, so nothing in
// memory can tell the next attempt "this already happened once" - only
// something that survives the reload can. Deliberately never cleared back
// to unset within the tab's lifetime either: a persistent error (a real
// bug, not a stale chunk) keeps landing on the fallback UI instead of
// reloading forever.
const RELOAD_ATTEMPTED_KEY = "errorBoundaryReloadAttempted";

/**
 * Catches a render-time error anywhere below it - today, that's everything
 * AppRouter.js renders, every React.lazy page included. Without this, an
 * uncaught error unmounts the WHOLE React tree: React has nowhere else to
 * put the fallback, so the result is a blank white page with nothing on it
 * to click.
 *
 * The specific failure this exists for: a lazy import() rejecting because
 * the index.html this tab loaded references a chunk hash a newer deploy has
 * since rotated out from under it (see AppRouter.js's React.lazy calls).
 * That hits hardest on a COLD load straight at a lazy route - a bookmark, a
 * typed URL, a link from outside the app - landing on a build that was
 * already stale the moment it loaded, rather than an in-app navigation
 * reusing a chunk manifest it already fetched successfully. A plain refresh
 * fixes it (a fresh index.html points at the current hashes), which is
 * exactly what this automates: reload once, the first time anything is ever
 * caught in this tab, rather than leaving a visitor staring at a blank page
 * with no indication anything is wrong or what to do about it.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
        return {hasError: true};
    }

    componentDidCatch() {
        // sessionStorage can throw (private browsing, storage disabled) -
        // treated the same as "already tried": show the fallback below
        // rather than risk reloading with no way to remember it happened.
        let alreadyTried = true;
        try {
            alreadyTried = sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === "1";
            if (!alreadyTried) sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, "1");
        } catch {}

        if (!alreadyTried) window.location.reload();
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        // Shown only once the automatic reload above either didn't fire
        // (already tried this tab) or hasn't taken effect yet - same visual
        // family as pages/Loading.js, since this is the same kind of "no
        // real content yet" full-page state.
        return (
            <div className="errorBoundaryPage">
                <div className="errorBoundaryCard">
                    <h1>Trackadillo</h1>
                    <p>Something went wrong loading this page.</p>
                    <button onClick={() => window.location.reload()}>Reload</button>
                </div>
            </div>
        );
    }
}
